/**
 * Question Component Registry
 *
 * This module registers all question component types with the factory.
 * Each registration includes the component and an adapter function that
 * transforms shared context into component-specific props.
 *
 * Import this module at app startup to ensure all components are registered.
 */

import i18n from '@/i18n';
import { questionComponentFactory } from './questionComponentFactory';
import TeamPick from '@/components/game/TeamPick';
import PlayerPick from '@/components/game/PlayerPick';
import PropPick from '@/components/game/PropPick';
import TieBreakerQuestion from '@/components/game/TieBreakerQuestion';
import OverUnderPick from '@low6-pickem/ui/components/OverUnderPick';
import CustomPick from '@low6-pickem/ui/components/CustomPick';
import LeaderPick from '@low6-pickem/ui/components/LeaderPick';
import { QUESTION_TYPES } from '@/constants/question-types';
import {
  getTeamsForQuestion,
  getCustomOptionsForQuestion,
  getPercentagesForPropQuestion,
  getPercentagesForOverUnderQuestion,
  getThirdImagesForPropQuestion,
  extractPlayerData,
  formatPlayerNameAsInitialSurname,
  extractStatData,
  extractJerseyUrl,
  extractPlayerTeamLogo,
  extractMatchTime,
  getPlayersForQuestion,
  extractTeamShortCodes,
  extractTeamLogos,
  formatMatchTime,
} from '@/utils/pickem-helpers';
import { getQuestionId } from '@/utils/id-helpers';

/** Locked option chrome while view-only and this pick is not graded yet. */
function isOptionLocked(isViewOnly: boolean, isCorrect: boolean | undefined): boolean {
  return isViewOnly && typeof isCorrect !== 'boolean';
}

// ============================================================================
// TEAM QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.TEAM, {
  component: TeamPick,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    const rawTeams = getTeamsForQuestion(context.question);
    const showPopularPicks = context.state.showPopularPicks === true;
    // When showPopularPicks is off, strip popularityPct from every team option
    // so the OptionPercentageStat badge is not rendered.
    const teams = showPopularPicks
      ? rawTeams
      : rawTeams.map(t => ({ ...t, popularityPct: undefined }));
    const isCorrect = context.state.selectionResults?.[questionId];
    const earnedPoints = context.state.selectionPoints?.[questionId];

    return {
      questionId,
      questionNumber: context.questionNumber,
      teams,
      points: context.question.points || 0,
      questionText: context.hooks.getField(context.question, 'title'),
      subtext: context.hooks.getField(context.question, 'description'),
      earnedPoints,
      selectedTeam: context.selections.teams[questionId],
      onTeamSelect: (teamId: string | undefined) =>
        context.handlers.onTeamSelect(questionId, teamId),
      isCorrect,
      disabled: context.state.isViewOnly,
      locked: isOptionLocked(context.state.isViewOnly, isCorrect),
      questionMetadata: context.question.question_metadata,
    };
  },
});

// ============================================================================
// PLAYER QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.PLAYER, {
  component: PlayerPick,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    const rawOptions = getPlayersForQuestion(context.question);
    const showPopularPicks = context.state.showPopularPicks === true;
    const options = showPopularPicks
      ? rawOptions
      : rawOptions.map((o: { popularityPct?: number; [key: string]: unknown }) => ({
          ...o,
          popularityPct: undefined,
        }));
    const isCorrect = context.state.selectionResults?.[questionId];
    const earnedPoints = context.state.selectionPoints?.[questionId];

    return {
      questionId,
      questionNumber: context.questionNumber,
      options,
      points: context.question.points || 0,
      earnedPoints,
      questionText: context.hooks.getField(context.question, 'title'),
      subtext: context.hooks.getField(context.question, 'description'),
      selectedTeam: context.selections.teams[questionId],
      onTeamSelect: context.state.isViewOnly
        ? () => {}
        : (teamId: string | undefined) => context.handlers.onTeamSelect(questionId, teamId),
      isCorrect,
      disabled: context.state.isViewOnly,
      locked: isOptionLocked(context.state.isViewOnly, isCorrect),
      questionMetadata: context.question.question_metadata,
    };
  },
});

// ============================================================================
// PROP QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.PROP, {
  component: PropPick,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    const teams = getTeamsForQuestion(context.question);
    const rawPercentages = getPercentagesForPropQuestion(context.question);
    const thirdImages = getThirdImagesForPropQuestion(context.question);
    const showPopularPicks = context.state.showPopularPicks === true;
    // When showPopularPicks is off, pass undefined so AnswerButton omits the badge.
    const percentages = showPopularPicks ? rawPercentages : undefined;
    const isCorrect = context.state.selectionResults?.[questionId];
    const earnedPoints = context.state.selectionPoints?.[questionId];

    return {
      questionId,
      questionNumber: context.questionNumber,
      teams,
      question: context.hooks.getField(context.question, 'title'),
      subtext: context.hooks.getField(context.question, 'description'),
      points: context.question.points || 0,
      earnedPoints,
      percentages,
      thirdImages,
      selectedAnswer: context.selections.props[questionId],
      onAnswerSelect: context.state.isViewOnly
        ? () => {}
        : (answer: 'yes' | 'no') => context.handlers.onPropSelect(questionId, answer),
      isCorrect,
      disabled: context.state.isViewOnly,
      locked: isOptionLocked(context.state.isViewOnly, isCorrect),
      questionMetadata: context.question.question_metadata,
    };
  },
});

// ============================================================================
// OVER/UNDER QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.OVER_UNDER, {
  component: OverUnderPick,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    // Main-market Total (DKPLAT-500) is match-level: no player/team subject, so
    // skip the player panel entirely and show the matchup instead — otherwise the
    // shared over/under layout falls back to a blank jersey + "U. Player".
    const isMatchLevel =
      String(context.question.question_type).toLowerCase() === QUESTION_TYPES.TOTAL;
    const playerData = extractPlayerData(context.question);
    const statData = extractStatData(context.question);
    const rawPercentages = getPercentagesForOverUnderQuestion(context.question);
    const teamNames = extractTeamShortCodes(context.question);
    const teamLogos = extractTeamLogos(context.question);
    const playerTeamLogo = extractPlayerTeamLogo(context.question);
    const showPopularPicks = context.state.showPopularPicks === true;
    const isCorrect = context.state.selectionResults?.[questionId];
    const earnedPoints = context.state.selectionPoints?.[questionId];

    return {
      questionId,
      questionNumber: context.questionNumber,
      points: context.question.points || 0,
      earnedPoints,
      isMatchLevel,
      playerName: isMatchLevel ? '' : formatPlayerNameAsInitialSurname(playerData.name),
      playerPosition: isMatchLevel ? '' : playerData.position,
      playerJerseyUrl: isMatchLevel ? '' : extractJerseyUrl(context.question),
      playerTeamLogoUrl: isMatchLevel ? undefined : playerTeamLogo,
      homeTeamLogoUrl: teamLogos.homeTeamLogo,
      awayTeamLogoUrl: teamLogos.awayTeamLogo,
      statValue: statData.threshold,
      statType: statData.displayName,
      homeTeamName: teamNames.homeTeamAbbreviation,
      awayTeamName: teamNames.awayTeamAbbreviation,
      matchTime: extractMatchTime(context.question),
      // When showPopularPicks is off, pass undefined so AnswerButtonOverUnder omits the badge.
      overPercentage: showPopularPicks ? rawPercentages.over : undefined,
      underPercentage: showPopularPicks ? rawPercentages.under : undefined,
      selectedAnswer: context.selections.overUnder?.[questionId],
      onAnswerSelect: (answer: 'over' | 'under' | undefined) => {
        if (context.handlers.onOverUnderSelect) {
          context.handlers.onOverUnderSelect(questionId, answer);
        }
      },
      // Natural-language question copy — same pattern as Team/Player/Custom.
      questionText: context.hooks.getField(context.question, 'title'),
      subtext: context.hooks.getField(context.question, 'description'),
      isCorrect,
      disabled: context.state.isViewOnly,
      locked: isOptionLocked(context.state.isViewOnly, isCorrect),
      questionMetadata: context.question.question_metadata,
    };
  },
});

// ============================================================================
// CUSTOM QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.CUSTOM, {
  component: CustomPick,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    const options = getCustomOptionsForQuestion(context.question);
    const isCorrect = context.state.selectionResults?.[questionId];
    const earnedPoints = context.state.selectionPoints?.[questionId];

    // `main_image` is the API field for the question-level image (DKPLAT-363).
    // Used for image-identification questions where the picture IS the prompt
    // (e.g. "Who is this coach?"). Not localised — single URL.
    //
    // Runtime-guarded narrow: PickemQuestion's generated type doesn't carry
    // main_image yet (added at the controller layer), so the cast is scoped
    // to just this property — not the whole question — and the string guard
    // keeps `null`, `number`, empty strings, or accidental objects out of
    // the <img src>.
    const rawImage = (context.question as { main_image?: unknown }).main_image;
    const questionImageUrl = typeof rawImage === 'string' && rawImage.length > 0 ? rawImage : null;

    const showPopularPicks = context.state.showPopularPicks === true;
    // When showPopularPicks is off, strip popularityPct from each option.
    const gatedOptions = showPopularPicks
      ? options
      : options.map(o => ({ ...o, popularityPct: undefined }));

    return {
      questionId,
      questionNumber: context.questionNumber,
      questionText: context.hooks.getField(context.question, 'title'),
      subtext: context.hooks.getField(context.question, 'description'),
      questionImageUrl,
      options: gatedOptions,
      selectedOption: context.selections.teams[questionId],
      onOptionSelect: context.state.isViewOnly
        ? () => {}
        : (optionId: string | undefined) => context.handlers.onTeamSelect(questionId, optionId),
      points: context.question.points || 0,
      earnedPoints,
      isCorrect,
      disabled: context.state.isViewOnly,
      locked: isOptionLocked(context.state.isViewOnly, isCorrect),
      questionMetadata: context.question.question_metadata,
    };
  },
});

// ============================================================================
// LEADER QUESTION REGISTRATION (DKPLAT-354)
// ============================================================================
// Cross-fixture player/team leader questions. Candidates + live stats are
// pre-populated on the option payload by the backend; this adapter just
// flattens them into LeaderPickProps. The component owns its own modal state.

const leaderAdapter = (kind: 'player' | 'team') => (context: any) => {
  const questionId = getQuestionId(context.question);
  const isCorrect = context.state.selectionResults?.[questionId];
  const earnedPoints = context.state.selectionPoints?.[questionId];
  const leaderboardMeta = context.question.question_metadata?.leaderboard;
  const statLabel: string = leaderboardMeta?.statLabel || leaderboardMeta?.statType || 'points';

  // Build candidates from option payload. We accept either an already-shaped
  // `option.leaderCandidate` (from a future backend enrichment) or raw fields
  // so old/new payloads both work.
  const options = (context.question.options || []).map((opt: any) => {
    const grading = opt.grading_data || opt.gradingData || {};
    // `liveValue` is written by the live-scoring grader once any fixture in
    // the contest window is live or finished — prefer it. `seasonValue` is
    // the season-to-date total written at admin save time; that's the
    // pre-game number to show before live data exists. Snake-case
    // `opt.live_value` is the legacy fallback used by older payloads.
    const statValue =
      typeof grading.liveValue === 'number'
        ? grading.liveValue
        : typeof grading.seasonValue === 'number'
          ? grading.seasonValue
          : typeof opt.live_value === 'number'
            ? opt.live_value
            : 0;
    // Pickem detail API exposes options with title_en / title_fr (matches
    // the getLocalizedField pattern that PlayerPick / TeamPick use). Use
    // context.hooks.getField to honour the user's selected locale; fall
    // through to snake_case admin payloads if used outside the standard
    // pickem-detail flow.
    const name =
      context.hooks.getField(opt, 'title') ||
      opt.title ||
      opt.option_text_en ||
      opt.optionTextEn ||
      opt.name ||
      i18n.t('question.unknown');
    return {
      id: opt.option_id || opt.id,
      name,
      position: grading.playerPosition,
      teamShortcode: grading.teamShortcode,
      imageUrl: opt.option_image || opt.option_image_en || opt.optionImageEn,
      teamLogoUrl: opt.option_third_image || opt.thirdImageEn,
      fixtureLabel: grading.fixtureLabel,
      // grading.matchTime is the raw ISO timestamp saved at question-build
      // time. Format it to "Mon, 1:00 PM" for the modal so it matches the
      // TeamPick / PlayerPick conventions.
      matchTime: grading.matchTime ? formatMatchTime(grading.matchTime) : undefined,
      statValue,
      popularityPct: opt.popularity_pct,
    };
  });

  // Backend sorts by stat desc when building, but defend against unsorted inputs.
  options.sort((a: any, b: any) => (b.statValue ?? 0) - (a.statValue ?? 0));

  const showPopularPicks = context.state.showPopularPicks === true;
  const gatedOptions = showPopularPicks
    ? options
    : options.map((o: any) => ({ ...o, popularityPct: undefined }));

  return {
    questionId,
    questionNumber: context.questionNumber,
    kind,
    statLabel,
    options: gatedOptions,
    selectedOptionId: context.selections.teams[questionId],
    onSelect: context.state.isViewOnly
      ? () => {}
      : (optionId: string | undefined) => context.handlers.onTeamSelect(questionId, optionId),
    points: context.question.points || 0,
    earnedPoints,
    questionText: context.hooks.getField(context.question, 'title'),
    subtext: context.hooks.getField(context.question, 'description'),
    isCorrect,
    disabled: context.state.isViewOnly,
    locked: isOptionLocked(context.state.isViewOnly, isCorrect),
    questionMetadata: context.question.question_metadata,
  };
};

questionComponentFactory.register(QUESTION_TYPES.PLAYER_LEADER, {
  component: LeaderPick,
  adapter: leaderAdapter('player'),
});

questionComponentFactory.register(QUESTION_TYPES.TEAM_LEADER, {
  component: LeaderPick,
  adapter: leaderAdapter('team'),
});

// ============================================================================
// TIEBREAKER QUESTION REGISTRATION
// ============================================================================

questionComponentFactory.register(QUESTION_TYPES.TIEBREAKER, {
  component: TieBreakerQuestion,
  adapter: context => {
    const questionId = getQuestionId(context.question);
    const earnedPoints = context.state.selectionPoints?.[questionId];

    return {
      questionId,
      title: context.hooks.t('tiebreaker.title'),
      description:
        context.hooks.getField(context.question, 'description') ||
        context.hooks.getField(context.question, 'title'),
      value: context.selections.tieBreaker,
      onChange: context.handlers.onTieBreakerChange,
      disabled: context.state.isViewOnly,
      points: context.question.points || 0,
      earnedPoints,
      questionMetadata: context.question.question_metadata,
    };
  },
});

// Log registered types in development
if (import.meta.env.DEV) {
  const registeredTypes = questionComponentFactory.getSupportedTypes();
  console.log('[QuestionComponentRegistry] Registered question types:', registeredTypes);
}
